import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listAccounts, type AccountDoc, fsTimestampToDate } from "../api/functions";

export function AccountsPage() {
  const [accounts, setAccounts] = useState<AccountDoc[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listAccounts()
      .then(r => setAccounts(r.accounts))
      .catch(e => setError(e?.message ?? "Failed to load accounts"));
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-800">Accounts</h1>
        <Link
          to="/admin/accounts/new"
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md px-4 py-2"
        >
          + New account
        </Link>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {accounts === null && !error && <div className="text-slate-500">Loading…</div>}

      {accounts && accounts.length === 0 && (
        <div className="text-slate-500 text-sm">No accounts yet. Click "New account" to create one.</div>
      )}

      {accounts && accounts.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {accounts.map(a => (
                <tr key={a.accountId} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link to={`/admin/accounts/${a.accountId}`} className="text-indigo-600 hover:underline">
                      {a.email}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{a.name}</td>
                  <td className="px-4 py-3">
                    <StatusPill status={a.status} />
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    {fsTimestampToDate(a.createdAt)?.toLocaleDateString() ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const isActive = status === "active";
  return (
    <span className={
      "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium " +
      (isActive ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700")
    }>
      {status}
    </span>
  );
}
